import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SiteFooter from '@/components/SiteFooter';
import {
  ArrowLeft, Scale, FileText, Shield, RefreshCw, LifeBuoy, Users, Ban, ChevronRight, AlertTriangle,
} from 'lucide-react';

const UPDATED = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const DOCS = [
  { to: '/terms', icon: FileText, title: 'Terms of Service', desc: 'The agreement governing your use of the platform, subscriptions and billing.', updated: UPDATED },
  { to: '/privacy', icon: Shield, title: 'Privacy Policy', desc: 'What information we collect, why, and the service providers we share it with.', updated: UPDATED },
  { to: '/refund-policy', icon: RefreshCw, title: 'Refund Policy', desc: 'How trials, renewals, cancellations and refunds work.', updated: UPDATED },
  { to: '/safety', icon: LifeBuoy, title: 'Safety Policy', desc: 'Responsible use, trading-risk awareness and reporting security concerns.', updated: UPDATED },
  { to: '/code-of-conduct', icon: Users, title: 'Code of Conduct', desc: 'Standards for respectful and responsible use of the platform.', updated: UPDATED },
  { to: '/acceptable-use', icon: Ban, title: 'Acceptable Use Policy', desc: 'Activities that are prohibited when using the platform and its integrations.', updated: UPDATED },
  { to: '/safety', icon: AlertTriangle, title: 'Important Legal Disclaimer', desc: 'The core educational-use and risk disclaimer shown on entry (summarized in Safety).', updated: UPDATED },
];

const LegalCenter = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="legal-center-page">
      <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700 safe-top">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-slate-400 hover:text-white" data-testid="back-to-app-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to App
          </Button>
          <div className="flex-1" />
          <Scale className="h-5 w-5 text-amber-400" />
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-white">Legal Center</h1>
          <p className="text-slate-400 mt-2 max-w-2xl leading-relaxed">
            All policies and legal documents for Option Wizard in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {DOCS.map((d) => (
            <Link
              key={d.title}
              to={d.to}
              data-testid={`legal-card-${d.to.replace('/', '')}`}
              className="group rounded-xl border border-slate-700 bg-slate-800/50 hover:bg-slate-800 hover:border-amber-500/40 transition-all duration-200 p-5 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <div className="p-2 bg-amber-500/15 rounded-lg">
                  <d.icon className="h-5 w-5 text-amber-400" />
                </div>
              </div>
              <h2 className="mt-3 text-lg font-semibold text-white group-hover:text-amber-200 transition-colors">{d.title}</h2>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed flex-1">{d.desc}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-slate-500">Updated {d.updated}</span>
                <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default LegalCenter;
